/**
 * @module useInstance
 * @description Custom hook for managing persistent values across component renders
 * using a global instance map
 */

import { useEffect } from 'react';

/**
 * Map to store all instances by their unique identifiers
 * @type {Map<string, any>}
 */
const instances = new Map();

/**
 * @function useInstance
 * @description Provides access to an instance value that persists across component renders
 * and can be shared between components using the same ID
 *
 * @param {string} id - Unique identifier for the instance
 * @param {*} initialValue - Initial value to set if the instance doesn't exist (defaults to null)
 * @returns {Array} - Array containing:
 *   [0] {Function} getInstance - Function to get the current value of the instance
 *   [1] {Function} setInstance - Function to update the value of the instance
 *
 * @example
 * const [getCounter, setCounter] = useInstance('sharedCounter', 0);
 *
 * // Get the current value
 * const currentValue = getCounter();
 *
 * // Update the value
 * setCounter(currentValue + 1);
 */
export default function useInstance(id, initialValue = null) {
  useEffect(() => {
    const setInitialValue = () => instances.set(id, initialValue);
    setInitialValue();
    return () => { setInitialValue(); };
  }, [id, initialValue]);

  /**
   * @function getInstance
   * @description Gets the current value of the instance
   * @returns {*} The current value of the instance
   */
  const getInstance = () => instances.get(id);

  /**
   * @function setInstance
   * @description Updates the value of the instance
   * @param {*} value - The new value to set
   * @returns {Map} The updated instances map
   */
  const setInstance = (value) => instances.set(id, value);

  return [getInstance, setInstance];
}
